import React, { useCallback, useEffect, useState } from 'react';
import api, { errMsg } from '../services/api';
import { fmt } from '../services/format';
import Msg from '../components/Msg';
import Pager from '../components/Pager';
import Modal from '../components/Modal';

const SIZE = 25;

function UserDetail({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/users/${id}`);
      setD(r.data);
    } catch (e) {
      setMsg({ type: 'err', text: errMsg(e) });
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okText, closeAfter) => {
    setBusy(true); setMsg(null);
    try {
      await fn();
      onChanged();
      if (closeAfter) { onClose(); return; }
      setMsg({ type: 'ok', text: okText });
      await load();
    } catch (e) {
      setMsg({ type: 'err', text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };

  const u = d?.user;
  const isSuper = u?.platformRole === 'superadmin';
  return (
    <Modal title={u ? u.displayName : 'Utilizator'} onClose={onClose}>
      <Msg msg={msg} />
      {!d && !msg && <p className="muted">Se încarcă…</p>}
      {d && (
        <>
          <p>{u.email} {isSuper && <span className="badge">superadmin</span>}</p>
          <p className="muted small">
            Creat {fmt(u.createdAt)} · ultima conectare {fmt(u.lastLoginAt)} ·{' '}
            {u.emailVerified ? 'email confirmat' : 'email NEconfirmat'} · {u.active ? 'cont activ' : 'cont dezactivat'}{u.locked ? ' · blocat temporar' : ''}
          </p>
          <h4>Familii</h4>
          {d.families.length === 0 ? <p className="muted small">Nu face parte din nicio familie.</p> : (
            <ul className="plain">
              {d.families.map(f => <li key={f.familyId}>{f.name} <span className="muted small">· {f.role}{f.status === 'invitat' ? ' · invitat' : ''}</span></li>)}
            </ul>
          )}
          {isSuper ? <p className="muted small">Conturile de superadmin nu se modifică de aici.</p> : (
            <div className="btn-row">
              {!u.emailVerified && (
                <button className="btn" disabled={busy}
                  onClick={() => act(() => api.post(`/api/users/${id}/verify-email`), 'Emailul a fost confirmat manual.')}>Confirmă emailul</button>
              )}
              {u.locked && (
                <button className="btn" disabled={busy}
                  onClick={() => act(() => api.post(`/api/users/${id}/unlock`), 'Contul a fost deblocat.')}>Deblochează</button>
              )}
              <button className="btn" disabled={busy}
                onClick={() => act(() => api.put(`/api/users/${id}/active`, { active: !u.active }),
                  u.active ? 'Contul a fost dezactivat și sesiunile închise.' : 'Contul a fost reactivat.')}>
                {u.active ? 'Dezactivează' : 'Reactivează'}
              </button>
              <button className="btn btn-danger" disabled={busy}
                onClick={() => window.confirm(`Ștergi definitiv contul ${u.email}?`)
                  && act(() => api.delete(`/api/users/${id}`), '', true)}>Șterge contul</button>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

export default function Users() {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/users', { params: { q: query, page, size: SIZE } });
      setData(r.data);
      setMsg(null);
    } catch (e) {
      setMsg({ type: 'err', text: errMsg(e) });
    }
  }, [query, page]);

  useEffect(() => { load(); }, [load]);

  const stateOf = u => {
    if (u.platformRole === 'superadmin') return <span className="badge">superadmin</span>;
    if (!u.active) return <span className="badge badge-suspendat">dezactivat</span>;
    if (u.locked) return <span className="badge badge-suspendat">blocat</span>;
    if (!u.emailVerified) return <span className="badge badge-pending">neconfirmat</span>;
    return <span className="badge badge-activ">activ</span>;
  };

  return (
    <div>
      <h2>Utilizatori</h2>
      <form className="toolbar" onSubmit={e => { e.preventDefault(); setPage(0); setQuery(q); }}>
        <input placeholder="Caută după nume sau email" value={q} onChange={e => setQ(e.target.value)} />
        <button className="btn">Caută</button>
      </form>
      <Msg msg={msg} />
      {!data && !msg && <p className="muted">Se încarcă…</p>}
      {data && (
        <>
          <div className="table-wrap">
            <table className="clickable">
              <thead><tr><th>Nume</th><th>Email</th><th>Familii</th><th>Stare</th><th>Ultima conectare</th></tr></thead>
              <tbody>
                {data.items.map(u => (
                  <tr key={u.userId} onClick={() => setOpenId(u.userId)}>
                    <td><b>{u.displayName}</b></td><td>{u.email}</td><td>{u.familyCount}</td>
                    <td>{stateOf(u)}</td><td>{fmt(u.lastLoginAt)}</td>
                  </tr>
                ))}
                {data.items.length === 0 && <tr><td colSpan={5} className="muted">Niciun utilizator.</td></tr>}
              </tbody>
            </table>
          </div>
          <Pager page={data.page} size={data.size} total={data.total} onPage={setPage} />
        </>
      )}
      {openId != null && <UserDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
